import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AdminShell } from "@/components/admin/AdminShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, MailOpen, MousePointerClick, Tag, CalendarCheck, Send, FileText, Sparkles, Download } from "lucide-react";
import { toCsv, downloadCsv } from "@/lib/csv";

type TLItem = {
  at: string; kind: string; title: string; detail?: string; meta?: any;
};

const KIND: Record<string, { icon: any; tone: string }> = {
  activity:   { icon: Sparkles, tone: "text-primary" },
  email_sent: { icon: Send,     tone: "text-emerald-400" },
  email_open: { icon: MailOpen, tone: "text-sky-400" },
  email_click:{ icon: MousePointerClick, tone: "text-fuchsia-400" },
  email_test: { icon: Send,     tone: "text-amber-400" },
  promo:      { icon: Tag,      tone: "text-yellow-400" },
  booking:    { icon: CalendarCheck, tone: "text-emerald-400" },
  submission: { icon: FileText, tone: "text-muted-foreground" },
};

export default function AdminLeadTimeline() {
  const { id } = useParams();
  const [lead, setLead] = useState<any>(null);
  const [items, setItems] = useState<TLItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      const { data: l } = await supabase.from("leads").select("*").eq("id", id).maybeSingle();
      setLead(l);
      const email = l?.email;

      const customerRes = email
        ? await supabase.from("customers").select("id").eq("email", email).maybeSingle()
        : { data: null as any };
      const customerId = customerRes?.data?.id ?? null;

      const [acts, runs, eng, reds, bks, subs] = await Promise.all([
        supabase.from("lead_activities").select("*").eq("lead_id", id).order("created_at", { ascending: false }),
        supabase.from("email_campaign_runs").select("*, email_campaigns(name)").eq("lead_id", id),
        supabase.from("email_engagements").select("*, email_campaigns(name), email_campaign_steps(subject,position)").eq("lead_id", id),
        email ? supabase.from("promo_redemptions").select("*, promo_codes(code)").eq("customer_email", email) : Promise.resolve({ data: [] as any[] }),
        customerId ? supabase.from("bookings").select("id,start_at,total,status,studio_id,attribution").eq("customer_id", customerId).order("created_at", { ascending: false }) : Promise.resolve({ data: [] as any[] }),
        supabase.from("landing_page_submissions").select("*, landing_pages(slug,title)").eq("lead_id", id),
      ] as const);

      const tl: TLItem[] = [];
      for (const a of (acts.data as any[]) ?? []) {
        tl.push({ at: a.created_at, kind: "activity", title: a.type, detail: a.body, meta: a.metadata });
      }
      for (const r of (runs.data as any[]) ?? []) {
        tl.push({ at: r.started_at, kind: "activity", title: `Enrolled in ${r.email_campaigns?.name ?? "campaign"}`,
          detail: `status=${r.status} · step=${r.current_step}` });
        if (r.completed_at) tl.push({ at: r.completed_at, kind: "activity", title: `Campaign ${r.status}`, detail: r.email_campaigns?.name });
      }
      for (const e of (eng.data as any[]) ?? []) {
        const label = `${e.email_campaigns?.name ?? "Campaign"} · step ${(e.email_campaign_steps?.position ?? 0) + 1}: ${e.email_campaign_steps?.subject ?? e.subject ?? ""}`;
        const kind = e.source === "test" ? "email_test" : "email_sent";
        tl.push({ at: e.sent_at, kind, title: `Email sent — ${label}`, detail: `to ${e.recipient_email}` });
        if (e.last_opened_at) tl.push({ at: e.last_opened_at, kind: "email_open", title: `Opened (${e.opens_count}×)`, detail: label });
        if (e.last_clicked_at) {
          const last = (e.clicked_urls ?? []).slice(-1)[0]?.url;
          tl.push({ at: e.last_clicked_at, kind: "email_click", title: `Clicked (${e.clicks_count}×)`, detail: last ?? label });
        }
      }
      for (const r of (reds.data as any[]) ?? []) {
        tl.push({ at: r.created_at, kind: "promo", title: `Redeemed ${r.promo_codes?.code ?? "promo"}`,
          detail: `−$${Number(r.amount_discounted).toFixed(2)} · source=${r.source ?? "—"}` });
      }
      for (const b of (bks.data as any[]) ?? []) {
        tl.push({ at: b.start_at, kind: "booking", title: `Booking ${b.status}`,
          detail: `$${Number(b.total).toFixed(2)} · ${new Date(b.start_at).toLocaleString()}`, meta: b });
      }
      for (const s of (subs.data as any[]) ?? []) {
        tl.push({ at: s.created_at, kind: "submission", title: `Landing form: /${s.landing_pages?.slug ?? "?"}`,
          detail: JSON.stringify(s.data ?? {}).slice(0, 120) });
      }
      tl.sort((a, b) => +new Date(b.at) - +new Date(a.at));
      setItems(tl);
      setLoading(false);
    })();
  }, [id]);

  const attr = lead?.attribution ?? {};

  return (
    <AdminShell title="Lead Timeline" actions={
      <div className="flex items-center gap-3">
        <Button size="sm" variant="outline" disabled={!lead || items.length === 0} onClick={() => exportTimelineCsv(lead, items)}>
          <Download className="w-4 h-4 mr-1" /> CSV
        </Button>
        <Link to="/admin/leads" className="text-xs underline">← Back to leads</Link>
      </div>
    }>
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : !lead ? (
        <p className="text-muted-foreground">Lead not found.</p>
      ) : (
        <>
          <div className="bg-surface border border-border p-4 mb-6">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div>
                <h2 className="font-display text-2xl">{lead.full_name ?? lead.email}</h2>
                <p className="text-muted-foreground text-sm font-mono">{lead.email} {lead.phone && `· ${lead.phone}`}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Badge variant="outline">source: {lead.source}</Badge>
                <Badge variant="outline">status: {lead.status}</Badge>
                {(lead.tags ?? []).map((t: string) => <Badge key={t} variant="secondary">{t}</Badge>)}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs font-mono">
              <Attr label="utm_source" v={attr.utm_source} />
              <Attr label="utm_medium" v={attr.utm_medium} />
              <Attr label="utm_campaign" v={attr.utm_campaign} />
              <Attr label="landing_slug" v={attr.landing_slug} />
              <Attr label="first_seen_at" v={attr.first_seen_at ? new Date(attr.first_seen_at).toLocaleString() : null} />
            </div>
          </div>

          <div className="bg-surface border border-border">
            {items.length === 0 ? (
              <div className="p-6 text-muted-foreground text-center">No activity yet.</div>
            ) : items.map((it, i) => {
              const k = KIND[it.kind] ?? KIND.activity; const I = k.icon;
              return (
                <div key={i} className="flex gap-3 p-3 border-b border-border last:border-b-0">
                  <I className={`w-4 h-4 mt-0.5 shrink-0 ${k.tone}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{it.title}</p>
                    {it.detail && <p className="text-xs text-muted-foreground font-mono truncate">{it.detail}</p>}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono shrink-0">{new Date(it.at).toLocaleString()}</div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </AdminShell>
  );
}

function Attr({ label, v }: { label: string; v?: string | null }) {
  return (
    <div className="bg-panel border border-border p-2">
      <div className="uppercase tracking-widest text-[10px] text-muted-foreground">{label}</div>
      <div className="truncate">{v || "—"}</div>
    </div>
  );
}

function exportTimelineCsv(lead: any, items: TLItem[]) {
  if (!lead) return;
  const attr = lead.attribution ?? {};
  const headerRows = [
    { section: "lead", at: "", kind: "info", title: "lead_email", detail: lead.email, meta: "" },
    { section: "lead", at: "", kind: "info", title: "full_name", detail: lead.full_name ?? "", meta: "" },
    { section: "lead", at: "", kind: "info", title: "source", detail: lead.source ?? "", meta: "" },
    { section: "lead", at: "", kind: "info", title: "status", detail: lead.status ?? "", meta: "" },
    { section: "first_touch", at: attr.first_seen_at ?? "", kind: "attribution", title: "utm_source", detail: attr.utm_source ?? "", meta: "" },
    { section: "first_touch", at: "", kind: "attribution", title: "utm_medium", detail: attr.utm_medium ?? "", meta: "" },
    { section: "first_touch", at: "", kind: "attribution", title: "utm_campaign", detail: attr.utm_campaign ?? "", meta: "" },
    { section: "first_touch", at: "", kind: "attribution", title: "utm_term", detail: attr.utm_term ?? "", meta: "" },
    { section: "first_touch", at: "", kind: "attribution", title: "utm_content", detail: attr.utm_content ?? "", meta: "" },
    { section: "first_touch", at: "", kind: "attribution", title: "landing_slug", detail: attr.landing_slug ?? "", meta: "" },
    { section: "first_touch", at: "", kind: "attribution", title: "referrer", detail: attr.referrer ?? "", meta: "" },
  ];
  const eventRows = items.map(it => ({
    section: "event", at: it.at, kind: it.kind, title: it.title, detail: it.detail ?? "", meta: it.meta ? JSON.stringify(it.meta) : "",
  }));
  const csv = toCsv([...headerRows, ...eventRows], ["section", "at", "kind", "title", "detail", "meta"]);
  const safe = (lead.email ?? lead.id).replace(/[^a-z0-9]/gi, "_");
  downloadCsv(`lead-timeline-${safe}.csv`, csv);
}